/**
 * Development-only startup instrumentation (User Timing API).
 *
 * Measures the real startup phases so the perceived-performance work can be
 * verified instead of guessed: React bootstrap, token hydration, `/auth/me`,
 * token refresh, router ready, first authenticated shell paint, first page.
 *
 * Rules:
 *  - NOTHING is logged outside development (`import.meta.env.DEV`), and the
 *    calls are no-ops in production so they cost nothing at runtime.
 *  - Only phase NAMES and DURATIONS are recorded. Never a token, header,
 *    password, email or any personal data.
 *  - Marks are also written to the Performance timeline, so the browser
 *    Performance panel shows them alongside navigation timings.
 */

const ENABLED = import.meta.env.DEV && typeof performance !== "undefined";

export type StartupPhase =
  | "react-bootstrap"
  | "token-hydration"
  | "auth-me"
  | "token-refresh"
  | "router-ready"
  | "authenticated-shell"
  | "first-page";

const PREFIX = "fad:";

/** Record a point in time for a startup phase. */
export function markStartup(phase: StartupPhase): void {
  if (!ENABLED) return;
  try {
    performance.mark(`${PREFIX}${phase}`);
  } catch {
    // Performance API unavailable (older Tauri webview) — instrumentation is optional.
  }
}

/**
 * Measure from navigation start to now and log it once, in development only.
 * `detail` must never contain user data — it is a short status word.
 */
export function measureStartup(phase: StartupPhase, detail?: string): void {
  if (!ENABLED) return;
  try {
    const name = `${PREFIX}${phase}`;
    performance.mark(name);
    const measure = performance.measure(`${name}:from-start`, undefined, name);
    const ms = Math.round(measure.duration);
    console.info(`⏱ startup ${phase}: ${ms}ms${detail ? ` (${detail})` : ""}`);
  } catch {
    // Ignore: measuring must never break startup.
  }
}

const measuredOnce = new Set<StartupPhase>();

/**
 * Same as `measureStartup`, but only the FIRST call for a phase is reported.
 * Used for phases whose component can remount (e.g. every page render mounts
 * PageContainer — only the first one is a startup measurement).
 */
export function measureStartupOnce(phase: StartupPhase, detail?: string): void {
  if (!ENABLED || measuredOnce.has(phase)) return;
  measuredOnce.add(phase);
  measureStartup(phase, detail);
}

/** Time an async startup step and report its own duration (dev only). */
export async function timeStartup<T>(phase: StartupPhase, run: () => Promise<T>): Promise<T> {
  if (!ENABLED) return run();
  const start = performance.now();
  try {
    return await run();
  } finally {
    const ms = Math.round(performance.now() - start);
    markStartup(phase);
    console.info(`⏱ startup ${phase}: ${ms}ms`);
  }
}
